import { useState, useEffect, useCallback } from "react";
import { X, Clock, CheckCircle, Trash2, Archive, RotateCcw, AlertTriangle, Filter, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_CONFIG = {
  created: { icon: Clock, label: "Created", className: "log-event-created" },
  completed: { icon: CheckCircle, label: "Completed", className: "log-event-completed" },
  uncompleted: { icon: RotateCcw, label: "Reopened", className: "log-event-uncompleted" },
  deleted: { icon: Trash2, label: "Deleted", className: "log-event-deleted" },
  archived: { icon: Archive, label: "Archived", className: "log-event-archived" },
  restored: { icon: RotateCcw, label: "Restored", className: "log-event-restored" },
};

const QUADRANT_LABELS = {
  "hi-impact-lo-effort": "High Impact / Low Effort",
  "hi-impact-med-effort": "High Impact / Med Effort",
  "hi-impact-hi-effort": "High Impact / High Effort",
  "med-impact-lo-effort": "Med Impact / Low Effort",
  "med-impact-med-effort": "Med Impact / Med Effort",
  "med-impact-hi-effort": "Med Impact / High Effort",
  "lo-impact-lo-effort": "Low Impact / Low Effort",
  "lo-impact-med-effort": "Low Impact / Med Effort",
  "lo-impact-hi-effort": "Low Impact / High Effort",
};

const FILTER_OPTIONS = [
  { value: "", label: "All Events" },
  { value: "created", label: "Created" },
  { value: "completed", label: "Completed" },
  { value: "deleted", label: "Deleted" },
  { value: "archived", label: "Archived" },
  { value: "restored", label: "Restored" },
];

function formatDateTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function formatDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function TaskLog({ open, onClose }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const fetchLog = useCallback(async (skip = 0, eventType = "") => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (eventType) params.event_type = eventType;
      const res = await axios.get(`${API}/task-log`, { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load task log");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setPage(0);
      fetchLog(0, filter);
    }
  }, [open, filter, fetchLog]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLog(newPage * PAGE_SIZE, filter);
  };

  if (!open) return null;

  // Group entries by date
  const grouped = {};
  entries.forEach((e) => {
    const dateKey = formatDate(e.event_at);
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(e);
  });
  const dateKeys = Object.keys(grouped);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="dialog-overlay" onClick={onClose} data-testid="task-log-overlay">
      <div className="dialog-content task-log-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="task-log-header">
          <div>
            <div className="dialog-title" style={{ margin: 0 }}>Task Log</div>
            <div className="task-log-subtitle">{total} events recorded</div>
          </div>
          <div className="task-log-actions">
            <button
              className={`task-log-filter-btn ${showFilter ? "active" : ""}`}
              onClick={() => setShowFilter(!showFilter)}
              data-testid="task-log-filter-btn"
            >
              <Filter size={13} />
              <span>{FILTER_OPTIONS.find(f => f.value === filter)?.label || "All"}</span>
              <ChevronDown size={11} />
            </button>
            <button className="bubble-action-btn" onClick={onClose} data-testid="task-log-close-btn">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filter dropdown */}
        {showFilter && (
          <div className="task-log-filter-dropdown" data-testid="task-log-filter-dropdown">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`task-log-filter-option ${filter === opt.value ? "active" : ""}`}
                onClick={() => { setFilter(opt.value); setShowFilter(false); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="task-log-content">
          {loading ? (
            <div className="task-log-empty">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="task-log-empty">
              <AlertTriangle size={20} />
              <span>No events found{filter ? ` for "${filter}"` : ""}. Task events will appear here as you create, complete, and manage tasks.</span>
            </div>
          ) : (
            dateKeys.map((dateKey) => (
              <div key={dateKey} className="task-log-date-group">
                <div className="task-log-date-label">{dateKey}</div>
                {grouped[dateKey].map((entry) => {
                  const config = EVENT_CONFIG[entry.event_type] || EVENT_CONFIG.created;
                  const Icon = config.icon;
                  return (
                    <div key={entry.id} className={`task-log-entry ${config.className}`} data-testid="task-log-entry">
                      <div className="task-log-icon">
                        <Icon size={13} />
                      </div>
                      <div className="task-log-entry-body">
                        <div className="task-log-entry-top">
                          <span className="task-log-task-name">{entry.task_name}</span>
                          <span className={`task-log-event-badge ${config.className}`}>{config.label}</span>
                          {entry.urgent && <span className="task-log-urgent-badge">Urgent</span>}
                        </div>
                        <div className="task-log-entry-meta">
                          {entry.category && (
                            <span className={`meta-badge meta-badge-${entry.category}`}>{entry.category}</span>
                          )}
                          {entry.quadrant && (
                            <span className="task-log-quadrant">{QUADRANT_LABELS[entry.quadrant] || entry.quadrant}</span>
                          )}
                          <span className="task-log-time">
                            {new Date(entry.event_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                          </span>
                        </div>
                        {(entry.event_type === "completed" || entry.event_type === "archived") && entry.task_created_at && (
                          <div className="task-log-entry-dates">
                            <span>Created: {formatDateTime(entry.task_created_at)}</span>
                            {entry.task_completed_at && <span>Completed: {formatDateTime(entry.task_completed_at)}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="task-log-pagination">
            <button
              disabled={page === 0}
              onClick={() => handlePageChange(page - 1)}
              className="task-log-page-btn"
            >
              Prev
            </button>
            <span className="task-log-page-info">Page {page + 1} of {totalPages}</span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => handlePageChange(page + 1)}
              className="task-log-page-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
